import { useCallback, useEffect, useRef } from 'react';
import { systemAlert } from 'shared/src/component';
import { useInstanceId } from 'shared/src/hooks';
import { useGoodsBasic } from '../queries/useGoodsBasic';
import { useNavigation } from '@react-navigation/native';
import { bridge, isWeb } from 'shared/src/utils';

/**
 * 운영어드민에서 넘어온 접근은 STOP_SALE 이어도 통과시킨다.
 *
 * 상품 검수 중에는 판매중지 상품의 상세를 열어봐야 한다. ably-webview-react 의 상품 상세
 * (`pages/goods/[sno]/index.page.tsx`)가 같은 이유로 같은 예외를 두고 있고, 값도 그쪽
 * 상수(`base/constants/ably.ts` 의 `ABLY_ADMIN_PATH` / `ABLY_REACT_ADMIN_PATH`)와 같다.
 * (판매자어드민 `seller-admin` 은 그쪽에서도 플레이스 상품 상세에만 예외로 들어가 있어 제외)
 *
 * ⚠️ 비교는 **origin** 으로 한다. 원본은 `document.referrer` 를 문자열 완전 일치로 보는데,
 * 그게 성립하는 이유는 요즘 브라우저 기본 referrer 정책(`strict-origin-when-cross-origin`)이
 * cross-origin 이동에서 경로를 떼고 origin 만 보내기 때문이다. 어드민이 정책을 느슨하게 바꿔
 * 경로까지 실어 보내면 완전 일치는 깨진다 — origin 비교는 두 경우 다 통과한다.
 */
const ADMIN_REFERRER_ORIGINS: ReadonlySet<string> = new Set([
  'https://admin.a-bly.com',
  'https://my.a-bly.com',
  'https://operation-admin.a-bly.com',
]);

/** 어드민에서 넘어온 진입인지. referrer 가 없는 환경(네이티브·정책상 빈 문자열)은 false. */
const isFromAdminReferrer = (): boolean => {
  if (!isWeb || typeof document === 'undefined') return false;
  try {
    return ADMIN_REFERRER_ORIGINS.has(new URL(document.referrer).origin);
  } catch {
    return false;
  }
};

// STOP_SALE 상품은 진입 즉시 안내 후 이전 화면으로 복귀시킨다.
// SOLD_OUT 등 다른 sale_type은 현행 유지.
export const useStopSaleGuard = () => {
  const { data: basic } = useGoodsBasic();
  const instanceId = useInstanceId();
  const navigation = useNavigation();

  const hasAlertedRef = useRef(false);

  const isStopSale = basic?.sale_type === 'STOP_SALE';

  const onPressConfirm = useCallback(() => {
    if (!instanceId) {
      return;
    }

    const canGoBack = navigation.canGoBack();

    if (canGoBack) {
      navigation.goBack();
      return;
    }
    bridge.closeInstance({ instanceId });
  }, [instanceId, navigation]);

  useEffect(() => {
    if (!isStopSale || hasAlertedRef.current) return;
    // 어드민 검수 트래픽은 막지 않는다. once 플래그를 소비하기 전에 판정한다.
    if (isFromAdminReferrer()) return;
    hasAlertedRef.current = true;

    systemAlert('판매 중인 상품이 아닙니다', undefined, [
      { text: '확인', onPress: onPressConfirm },
    ]);
  }, [isStopSale, onPressConfirm]);
};
